import { action, computed, observable } from 'mobx';

import { StringKeyMap } from '../adl-gen/common';
import { DbKey, WithDbId } from '../adl-gen/common/db';
import {
  AppUser,
  CompetencySkillTrainingOffering,
  LearningProvider,
  Quiz,
  RecommendationType_Priority,
  RoleRecommendationMethod,
  TrainingOffering,
  TrainingOfferingCategory,
  UserGroup
} from '../adl-gen/totalinteraction/futurefit/db';
import {
  ComparisonRoles,
  MultiUserQuizResults,
  MultiUserTrainingRecommendations,
  Recommendation,
  SingleUserTrainingRecommendations,
  UsersGroupsMap
} from '../adl-gen/totalinteraction/futurefit/frontend';
import { QuizReportSummary } from '../adl-gen/totalinteraction/futurefit/requests';
import { fromPromise, MaybeLoading } from '../loader/loader';
import { Service } from '../service/service';

import { LoggedIn } from './identity-store';
import {
  LearningType,
  multiUserQuizResultsToQuizReportResults,
  QuizResultsHelper
} from './quiz-results-store';

export type SelectedResults =
  | {
    kind: 'users';
    users: string[];
  }
  | {
    kind: 'groups';
    groups: string[];
  };

export class ManagerQuizResultsStore {
  @observable rawInfo: Promise<RawInfo>;

  constructor(
    private readonly service: Service,
    private readonly identity: LoggedIn,
    readonly quizId: DbKey<Quiz>,
    readonly settings?: Settings
  ) // readonly roles?: string[]
  {
    const appUser = this.identity.appUserWithDbId;
    this.rawInfo = this.loadAll(appUser, quizId);
  }

  @computed
  get loading(): MaybeLoading<ManagerInfo> {
    return fromPromise(this.rawInfo.then(this.applyFilters));
  }

  usersFromGroups(groups: DbKey<UserGroup>[], usersGroupsMap: UsersGroupsMap): DbKey<AppUser>[] {
    const users: { [user: string]: null } = {};
    for (const g of groups) {
      for (const u of usersGroupsMap.usersByGroup[g]) {
        users[u] = null;
      }
    }
    return Object.keys(users);
  }

  /**
   *
   * NB: The 'appUserId' passed in is that for the *viewer* not the results user.
   * TODO: Function should be passing out multiple UserTrainingOFferingsData items.
   * So, bypass for non-priority results altogether and fix later
   */
  convertMultipleToSingleRecommendationSet(
    qr: QuizResultsHelper,
    viewerAppUserId: DbKey<AppUser>,
    quizTrainingRecommendations: MultiUserTrainingRecommendations
  ): SingleUserTrainingRecommendations {
    const aggregatePriorityRecommendations: { [key: string]: Recommendation } = {};
    let recommendations: Recommendation[];
    const defaultRole = qr.getDefaultRole();
    // We are only looking at priority/relevance recs not role/competency/range recs here
    if (defaultRole && defaultRole.value.recommendationMethod === RoleRecommendationMethod.relevance) {
      for (const offering of quizTrainingRecommendations.users) {
        for (const currentRec of offering.recommendations) {
          // Just do all this for priority recommendations ONLY
          // Problem here is that for priority aggregate recs are keyed on T/O ONLY
          if (currentRec.result.kind === 'priority') {
            // This line is just to init field on first pass
            // tslint:disable-next-line: strict-type-predicates
            if (aggregatePriorityRecommendations[currentRec.trainingOfferingId] === undefined) {
              aggregatePriorityRecommendations[currentRec.trainingOfferingId] = currentRec;
            } else {
              (aggregatePriorityRecommendations[currentRec.trainingOfferingId]
                .result as RecommendationType_Priority).value += (currentRec.result as RecommendationType_Priority).value;
            }
          }
        }
      }
      recommendations = Object.values(aggregatePriorityRecommendations);
    } else {
      // there's no default role (unlikely) OR these are role/competency/range recs: gather all into one big array with duplicates
      const recsWithDuplicates = quizTrainingRecommendations.users
        .map(user => user.recommendations)
        .reduce((acc, v) => acc.concat(v));

      const aggregateRoleRangeRecommendations: { [key: string]: Recommendation } = {};
      for (const rec of recsWithDuplicates) {
        if (rec.result.kind === 'roleScore') {
          // construct what should be a unique key
          const key = `${rec.trainingOfferingId}|${rec.result.value.skillOrCompetency}|${rec.result.value.workRoleId}`;
          // tslint:disable-next-line: strict-type-predicates
          if (aggregateRoleRangeRecommendations[key] === undefined) {
            aggregateRoleRangeRecommendations[key] = rec;
          }
        }
      }
      // aggregate is a map and should not have duplicates
      recommendations = Object.values(aggregateRoleRangeRecommendations);
    }

    const aggregateTrainingOfferings: SingleUserTrainingRecommendations = {
      user: {
        appUserId: viewerAppUserId,
        competencySkillMap: quizTrainingRecommendations.users
          .map(o => o.competencySkillMap)
          .reduce((c, m) => {
            const newMap = {
              ...m,
              ...c
            };
            return newMap;
          }, {}),
        recommendations
      },
      data: quizTrainingRecommendations.data
    };

    return aggregateTrainingOfferings;
  }

  /** @deprecated if we already filter by provider in API call */
  filteredRecommendationsByProvider = (
    providerIds: DbKey<LearningProvider>[],
    userTrainingRecommendations: SingleUserTrainingRecommendations
  ): SingleUserTrainingRecommendations => {
    if (providerIds.length === 0) {
      return userTrainingRecommendations;
    } else {
      const { appUserId, competencySkillMap, recommendations } = userTrainingRecommendations.user;
      const providerData: StringKeyMap<DbKey<TrainingOffering>, TrainingOffering> = {};

      for (const [k, v] of Object.entries(userTrainingRecommendations.data)) {
        if (providerIds.includes(v.providerId)) {
          providerData[k] = v;
        }
      }

      const providerRecommendations = recommendations.filter(r =>
        Object.keys(providerData).find(k => k === r.trainingOfferingId)
      );

      const providerCompetencySkillData: StringKeyMap<string, CompetencySkillTrainingOffering[]> = {};
      for (const [k, v] of Object.entries(competencySkillMap)) {
        const includedRec = providerRecommendations.find(rec =>
          v.map(item => item.value.trainingOfferingId).includes(rec.trainingOfferingId)
        );
        if (includedRec) {
          providerCompetencySkillData[k] = competencySkillMap[k];
        }
      }

      return {
        user: { appUserId, competencySkillMap: providerCompetencySkillData, recommendations: providerRecommendations },
        data: providerData
      };
    }
  };

  filterRecommendationsByCategory = (
    userTrainingRecommendations: SingleUserTrainingRecommendations,
    categories?: TrainingOfferingCategory[]
  ): SingleUserTrainingRecommendations => {
    if (categories === undefined || categories.length === 0) {
      return userTrainingRecommendations;
    }
    const { appUserId, competencySkillMap, recommendations } = userTrainingRecommendations.user;
    // Reduce recommendations if category filter set
    const filteredRecommendations: Recommendation[] = recommendations.reduce((arr: Recommendation[], next) => {
      const offering = userTrainingRecommendations.data[next.trainingOfferingId];
      if (categories.includes(offering.category)) {
        arr.push(next);
      }
      return arr;
    }, []);
    return {
      user: { appUserId, competencySkillMap, recommendations: filteredRecommendations },
      data: userTrainingRecommendations.data
    };
  };

  filterRecommendationsByCompetency = (
    userTrainingRecommendations: SingleUserTrainingRecommendations,
    competencies?: string[]
  ): SingleUserTrainingRecommendations => {
    if (competencies === undefined || competencies.length === 0) {
      return userTrainingRecommendations;
    }
    const { appUserId, competencySkillMap, recommendations } = userTrainingRecommendations.user;
    // Reduce CompetencySkillsMap
    const filteredCompetencySkillData: StringKeyMap<string, CompetencySkillTrainingOffering[]> = {};
    for (const k of Object.keys(competencySkillMap)) {
      if (competencies.includes(k)) {
        filteredCompetencySkillData[k] = competencySkillMap[k];
      }
    }
    // prevent duplicates when filtering recs
    const filteredRecommendationMap: StringKeyMap<string, Recommendation> = Object.values(
      filteredCompetencySkillData
    ).reduce((acc: StringKeyMap<string, Recommendation>, v) => {
      const csMapOfferings = v.map(item => item.value.trainingOfferingId);

      for (const rec of recommendations) {
        if (csMapOfferings.includes(rec.trainingOfferingId)) {
          acc[rec.trainingOfferingId] = rec;
        }
      }

      return acc;
    }, {});

    return {
      user: {
        appUserId,
        competencySkillMap,
        recommendations: Object.values(filteredRecommendationMap)
      },
      data: userTrainingRecommendations.data
    };
  };

  applyFilters = (rawInfo: RawInfo): ManagerInfo => {
    const appUserId = this.identity.appUserWithDbId.id;
    const providerIds = this.identity.appClient.providerIds;
    const defaultSelectedResults = rawInfo.selectedResults;
    const usersGroupsMap = rawInfo.usersGroupsMap;
    const allQuizReportResults: MultiUserQuizResults = rawInfo.quizReportResults;
    const allUserTrainingRecommendations: MultiUserTrainingRecommendations = rawInfo.quizTrainingOfferings;
    const usersCompleted = rawInfo.usersCompleted;

    const selectedResults =
      this.settings !== undefined && this.settings.filters !== undefined
        ? this.settings.filters
        : defaultSelectedResults;

    const includedAppUserIds: string[] =
      selectedResults.kind === 'users'
        ? selectedResults.users
        : this.usersFromGroups(selectedResults.groups, usersGroupsMap);

    const filteredQuizReportResults: MultiUserQuizResults = {
      ...allQuizReportResults,
      quizResults: allQuizReportResults.quizResults.filter(results => includedAppUserIds.includes(results.appUserId))
    };

    // get MultiUserTrainingRecommendations for just the filtered users...
    const filteredUserTrainingRecommendations: MultiUserTrainingRecommendations = {
      ...allUserTrainingRecommendations,
      users: allUserTrainingRecommendations.users.filter(user => includedAppUserIds.includes(user.appUserId))
    };

    // Overwrite usersCompleted to preserve correct value for quizresultshelper
    const quizReportResults = {
      ...multiUserQuizResultsToQuizReportResults(filteredQuizReportResults),
      usersCompleted
    };

    // SS-933: Remove role comparison from Managers
    const qr = QuizResultsHelper.fromQuizReportResults(quizReportResults, rawInfo.roles);

    if (this.settings && this.settings.role) {
      qr.setSelectedRoleId(this.settings.role);
    }

    const aggregateTrainingOfferings = this.convertMultipleToSingleRecommendationSet(
      qr,
      appUserId,
      filteredUserTrainingRecommendations
    );

    const aggregateTrainingOfferingsByProvider = this.filteredRecommendationsByProvider(
      providerIds,
      aggregateTrainingOfferings
    );

    // Enhanced recommendations
    let filteredAggregateTrainingOfferings = aggregateTrainingOfferingsByProvider;
    if (this.settings !== undefined) {
      filteredAggregateTrainingOfferings = this.filterRecommendationsByCategory(
        filteredAggregateTrainingOfferings,
        this.settings.categories
      );
      filteredAggregateTrainingOfferings = this.filterRecommendationsByCompetency(
        filteredAggregateTrainingOfferings,
        this.settings.competencies
      );
      qr.setPriorityFilter(this.settings.priorities);
    }

    qr.debugReports(this.identity.appClient.debugReporting);

    return {
      usersGroupsMap,
      qr,
      selectedResults,
      filteredCategories: this.settings && this.settings.categories ? this.settings.categories : [],
      filteredCompetencies: this.settings && this.settings.competencies ? this.settings.competencies : [],
      filteredPriorities: this.settings && this.settings.priorities ? this.settings.priorities : [],
      aggregateTrainingOfferings: filteredAggregateTrainingOfferings,
      guidanceAvailable: rawInfo.guidanceAvailable,
      recommendationsAvailable: rawInfo.recommendationsAvailable,
      showIndustryAverage: rawInfo.showIndustryAverage,
      hideExpected: rawInfo.hideExpected
    };
  };

  @computed
  get filterApplied(): boolean {
    return (
      this.settings !== undefined &&
      this.settings.filters !== undefined &&
      this.loading.kind === 'value' &&
      !isDefaultSelectedResults(this.settings.filters, this.loading.value.qr.getUsersCompleted())
    );
  }

  // This is too hard to implement properly so just return true if categories set
  @computed
  get categoryFilterApplied(): boolean {
    return (
      this.settings !== undefined &&
      (this.settings.categories !== undefined && this.settings.categories.length !== 0) &&
      this.loading.kind === 'value'
    );
  }
  @computed
  get competencyFilterApplied(): boolean {
    return (
      this.settings !== undefined &&
      (this.settings.competencies !== undefined && this.settings.competencies.length !== 0) &&
      this.loading.kind === 'value'
    );
  }
  @computed
  get priorityFilterApplied(): boolean {
    return (
      this.settings !== undefined &&
      (this.settings.priorities !== undefined && this.settings.priorities.length !== 0) &&
      this.loading.kind === 'value'
    );
  }

  @computed
  get guidanceAvailable(): boolean {
    return this.loading.kind === 'value' && this.loading.value.guidanceAvailable;
  }

  @computed
  get recommendationsAvailable(): boolean {
    return this.loading.kind === 'value' && this.loading.value.recommendationsAvailable;
  }

  @computed
  get showIndustryAverage(): boolean {
    return this.loading.kind === 'value' && this.loading.value.showIndustryAverage;
  }

  @computed
  get hideExpected(): boolean {
    return this.loading.kind === 'value' && this.loading.value.hideExpected;
  }

  @action
  loadAll = async (appUser: WithDbId<AppUser>, quizId: DbKey<Quiz>): Promise<RawInfo> => {
    const usersGroupsMap = await this.service.queryQuizReportParticipants(appUser.id, quizId);
    const quizReportSummaries = await this.service.queryQuizReports(appUser.id);

    const quizReportSummaryMatches = quizReportSummaries.items.filter(
      (qrs: QuizReportSummary) => qrs.quizId === this.quizId
    );

    if (quizReportSummaryMatches.length === 0) {
      throw Error("Quiz either doesn't exist or is not accessible by the user");
    }

    // (1) wrapper for queryQuizReportMulti
    const fetchQuizReportResults = async () => {
      const multiQuizReport = await this.service.queryQuizReportMulti(
        appUser.id,
        quizId,
        Object.keys(usersGroupsMap.appUsers)
      );
      // Ensure the role ranges are sorted correctly...
      multiQuizReport.quizMapAndScores.ranges.sort((a, b) =>
        a.scoreRange.range.v1 < b.scoreRange.range.v1 ? -1 : a.scoreRange.range.v1 > b.scoreRange.range.v1 ? 1 : 0
      );
      // ... and return the result
      return multiQuizReport;
    };

    // Get recommendation method on (default) role for getting training
    const recommendationMethod = quizReportSummaryMatches[0].quiz.workRole.recommendationMethod;

    // (2) wrapper for queryQuizTrainingRecommendations
    const fetchTrainingOfferingsData = async () => {
      return this.service.queryQuizTrainingRecommendations(
        appUser.id,
        quizId,
        recommendationMethod,
        this.identity.appClient.providerIds
      );
    };

    // (3) FF2-501 Query Group Results
    const fetchGroupResultsScoreSet = async () => {
      return this.service.queryGroupResultsScoreSet(appUser.value.appClientId, quizId);
    };

    const parallelFetchWrapper = async () => {
      return Promise.all([fetchQuizReportResults(), fetchGroupResultsScoreSet(), fetchTrainingOfferingsData()]);
    };

    // ... and (4) await the wrapper call
    const [quizReportResults, groupResultScoreSet, quizTrainingOfferings] = await parallelFetchWrapper();

    const quizReportResultsWithGroup: MultiUserQuizResults = {
      quizResults: quizReportResults.quizResults,
      quizMapAndScores: {
        ...quizReportResults.quizMapAndScores,
        datasets: {
          ...quizReportResults.quizMapAndScores.datasets,
          group: groupResultScoreSet
        }
      }
    };

    const roles = await this.service.queryComparisonRoles();

    const usersCompleted = quizReportResultsWithGroup.quizResults.map(r => r.appUserId);

    const selectedResults: SelectedResults = getDefaultSelectedResults(
      usersCompleted
    );

    /// FF2-427/FF2-429 - use quizReportSummaryMatches cos we've just checked to ensure it's non-empty
    const {
      guidanceAvailable,
      recommendationsAvailable,
      showIndustryAverage,
      hideExpected
    } = quizReportSummaryMatches[0].quiz;

    return {
      usersGroupsMap,
      roles,
      selectedResults,
      usersCompleted,
      quizReportResults: quizReportResultsWithGroup,
      quizTrainingOfferings,
      guidanceAvailable,
      recommendationsAvailable,
      showIndustryAverage,
      hideExpected
    };
  };

  getSettings(): Settings | undefined {
    return this.settings;
  }

  // This is cloned from QuizResultsStore so that it can be used for Managers.
  // Would be better in QuizResultsHelper except that Service is required
  // Could return 'executeUrl'... Or could point back to the server and
  // let the server do some stuff and then send a 303 See Other redirect with the real 'executeUrl'...
  // (Which can be a single use URL)
  // ISSUE: the return value is Promise<string>|string, but the promise is not necessarily correctly handled
  getTrainingOfferingUrl = (
    offering: WithDbId<TrainingOffering>,
    userId: DbKey<AppUser>,
    phoneHome: boolean = true
  ) => {
    // Don't phone home if not set up to do so. Enforce by using catalog entries; if there isn't one, assume phoning home won't work.
    // TODO: 'phonehome' is currently un-programmed awaiting a more sophisticated fix (eg. flag on learning providers)
    if (phoneHome && offering.value.catalogEntryId) {
      return this.service.launchTrainingOfferingUrl(offering.id, userId);
    } else {
      return offering.value.executeUrl;
    }
  };
}

export interface Settings {
  filters: SelectedResults | undefined;
  categories: TrainingOfferingCategory[] | undefined;
  competencies: string[] | undefined;
  priorities: LearningType[] | undefined;
  role: string | undefined;
  guidanceAvailable: boolean | undefined;
  recommendationsAvailable: boolean | undefined;
}

export function getDefaultSelectedResults(usersCompleted: DbKey<AppUser>[]): SelectedResults {
  return {
    kind: 'users',
    users: usersCompleted
  };
}

export function isDefaultSelectedResults(selected: SelectedResults, usersCompleted: DbKey<AppUser>[]): boolean {
  const defaultSelection: SelectedResults = getDefaultSelectedResults(usersCompleted);
  if (selected.kind === defaultSelection.kind && selected.kind === 'users' && defaultSelection.kind === 'users') {
    const selectionSet = new Set<string>(selected.users);
    const defaultSet = new Set<string>(defaultSelection.users);
    return (
      selectionSet.values.length === defaultSet.values.length &&
      isSubset(selectionSet, defaultSet) &&
      isSubset(defaultSet, selectionSet)
    );
  }
  return false;
}

export function selectedResultsAreValid(selected: SelectedResults) {
  if (selected.kind === 'users' && selected.users.length === 0) {
    return false;
  } else if (selected.kind === 'groups' && selected.groups.length === 0) {
    return false;
  }
  return true;
}
export function isSubset<T>(s1: Set<T>, s2: Set<T>) {
  for (const v of Array.from(s1)) {
    if (!s2.has(v)) {
      return false;
    }
  }
  return true;
}

export interface ManagerInfo {
  qr: QuizResultsHelper;
  usersGroupsMap: UsersGroupsMap;
  selectedResults: SelectedResults;
  aggregateTrainingOfferings: SingleUserTrainingRecommendations;
  guidanceAvailable: boolean;
  recommendationsAvailable: boolean;
  showIndustryAverage: boolean;
  hideExpected: boolean;
  // Enhanced recommendations
  filteredCategories: TrainingOfferingCategory[];
  filteredCompetencies: string[];
  filteredPriorities: LearningType[];
}

export interface RawInfo {
  quizReportResults: MultiUserQuizResults;
  roles: ComparisonRoles;
  usersGroupsMap: UsersGroupsMap;
  usersCompleted: DbKey<AppUser>[];
  selectedResults: SelectedResults;
  quizTrainingOfferings: MultiUserTrainingRecommendations;
  guidanceAvailable: boolean;
  recommendationsAvailable: boolean;
  showIndustryAverage: boolean;
  hideExpected: boolean;
}



// WEBPACK FOOTER //
// ./src/stores/manager-quiz-results-store.ts